import { isDefined } from "@clipboard-health/util-ts";
import {
  addHours,
  endOfDay,
  // eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
  format,
  getHours,
  getMinutes,
  parse,
  setHours,
  setMinutes,
} from "date-fns";
import pluralize from "pluralize";

import { VisitPricingTypes } from "./constants";
import { type VisitWithCaseData } from "./types";

export function getRegularPerHourVisitTimeRangeLabel(visit: VisitWithCaseData) {
  const { estimatedStartTime, estimatedDuration } = visit.attributes;
  // Old visits may not have an estimated start time. In this case we default to 12:00 PM.
  const estimatedStartTimeDate = parse(estimatedStartTime ?? "12:00 PM", "hh:mm a", new Date());
  // estimated duration is in hours and always exists for REGULAR PER_HOUR visits
  const estimatedEndTimeDate = addHours(estimatedStartTimeDate, estimatedDuration!);
  return `${format(estimatedStartTimeDate, "h:mm a")} - ${format(estimatedEndTimeDate, "h:mm a")}`;
}

export function obscureAddress(address: string): string {
  const addressComponents = address.split(",");
  if (addressComponents.length === 4) {
    return addressComponents.slice(1).join(",").trim();
  }

  return address;
}

export function getHighlightedVisitActionDeadlineTime(visit: VisitWithCaseData): Date | undefined {
  if (!isDefined(visit.attributes.confirmedVisitDate)) {
    return undefined;
  }

  const actionDeadline = new Date(visit.attributes.confirmedVisitDate);
  // if the visit isn't a per-hour visit, or the specific time of day is not defined
  // according to the visit deadlinethe HCP is flexible to when they can log/action the visit
  // therefore we give them until the end of the day to take action
  if (
    visit.attributes.pricingType !== VisitPricingTypes.PER_HOUR ||
    !isDefined(visit.attributes.deadline)
  ) {
    return endOfDay(actionDeadline);
  }

  // if the visit is a per-hour visit, there is a specific time for the action that we
  // extract from the visit deadline
  const hours = getHours(new Date(visit.attributes.deadline));
  const minutes = getMinutes(new Date(visit.attributes.deadline));
  return setHours(setMinutes(actionDeadline, minutes), hours);
}

export function getVisitWeeklyFrequencyAndDurationLabel(visit: VisitWithCaseData): string {
  const { visitsPerWeek, durationInWeeks } = visit.attributes;
  // Ex: 3 visits per week for 2 weeks
  return `${visitsPerWeek} ${pluralize(
    "visit",
    visitsPerWeek
  )} per week for ${durationInWeeks} ${pluralize("week", durationInWeeks)}`;
}
